import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";

const Group = ({ title, options, picked, toggle, testid }) => (
  <div data-testid={testid}>
    <p className="text-xs font-semibold text-muted-foreground mb-1.5">{title}</p>
    {options.length === 0 && <p className="text-xs text-muted-foreground">No email addresses on file.</p>}
    <div className="space-y-1.5">
      {options.map((o) => (
        <label key={o.email} className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={picked.includes(o.email)} onCheckedChange={() => toggle(o.email)} data-testid={`receipt-recipient-${o.email}`} />
          <span className="font-medium">{o.label}</span><span className="text-muted-foreground text-xs">{o.email}</span>
        </label>
      ))}
    </div>
  </div>
);

export const EmailReceiptDialog = ({ receipt, open, onOpenChange }) => {
  const qc = useQueryClient();
  const [picked, setPicked] = useState([]);
  const [manual, setManual] = useState("");
  const { data } = useQuery({
    queryKey: ["receipt-email-recipients", receipt?.id],
    queryFn: async () => (await api.get(`/posted-receipts/${receipt.id}/email-recipients`)).data,
    enabled: open && !!receipt,
  });
  useEffect(() => {
    if (!open) { setPicked([]); setManual(""); }
  }, [open]);
  const toggle = (e) => setPicked((p) => (p.includes(e) ? p.filter((x) => x !== e) : [...p, e]));
  const manualList = manual.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
  const all = [...new Set([...picked, ...manualList])];
  const send = useMutation({
    mutationFn: async () => (await api.post(`/posted-receipts/${receipt.id}/send-email`, { recipients: all })).data,
    onSuccess: (r) => { toast.success(`Receipt emailed to ${(r.sent_to || []).join(", ")}`); qc.invalidateQueries({ queryKey: ["posted-receipts"] }); onOpenChange(false); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-sm" data-testid="email-receipt-dialog">
        <DialogHeader>
          <DialogTitle>Email {receipt?.receipt_no}</DialogTitle>
          <DialogDescription>The receipt PDF is attached. Pick who should get it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[50vh] overflow-y-auto">
          <Group title="Vendor" options={data?.vendor || []} picked={picked} toggle={toggle} testid="receipt-recipients-vendor" />
          <Group title="Your team" options={data?.team || []} picked={picked} toggle={toggle} testid="receipt-recipients-team" />
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-1.5">Other email addresses</p>
            <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="warehouse@yourcompany.com, …" className="rounded-sm" data-testid="receipt-recipient-manual" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-sm" disabled={all.length === 0 || send.isPending} onClick={() => send.mutate()} data-testid="email-receipt-send">
            {send.isPending ? "Sending…" : all.length ? `Send to ${all.length} recipient${all.length === 1 ? "" : "s"}` : "Select recipients"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
